/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import { ContextualAnalyticsData, FireScreenAnalytics, MODAL } from '@atlassian/analytics-bridge';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import type { RecommendStandardOverPremiumModalProps } from '../../types';
import { getDaysUntil } from '../../utils/get-days-until';

import { FeatureComparisonPanel } from './feature-comparison-panel';
import { messages } from './messages';
import { PriceContrast } from './price-contrast';

const purpleStyles = unboundedCssMap({
	root: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#AF59E0',
	},
});

export { type RecommendStandardOverPremiumModalProps };

/**
 * A two-panel modal with UI matching these designs:
 * https://www.figma.com/design/gN2uHK0wPD93kkqExPlNeM/Confluence%3A-Recommend-Standard-to-RT-trialing-customers-(RT)?node-id=1-3&node-type=canvas&t=ZS7oExyVxcrlRkJQ-0
 *
 * This is intended to be used by Confluence as well as the Jira versions of the experiment.
 */

export const RecommendStandardOverPremiumModal = ({
	productKey,
	standardPrice,
	premiumPrice,
	trialEndDate,
	analyticsAttributes,
	onClose,
	onClickBuyStandard,
	onOpenComplete,
	testId,
}: RecommendStandardOverPremiumModalProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const percentageOff = Math.round(100 * (1 - standardPrice / premiumPrice));
	const daysLeftInTrial = getDaysUntil(trialEndDate);

	return (
		<ContextualAnalyticsData
			sourceName="recommendStandardOverPremiumModal"
			sourceType={MODAL}
			attributes={analyticsAttributes}
		>
			<Modal
				testId={testId}
				includeCloseButton
				onClose={onClose}
				onOpenComplete={onOpenComplete}
				title={
					<FormattedMessage
						{...messages.title}
						values={{
							purple: (chunks) => <span css={purpleStyles.root}>{chunks}</span>,
							percentageOff,
						}}
					/>
				}
				subtitle={formatMessage(messages.subtitle, { daysLeftInTrial })}
				height="536px"
				right={
					<FeatureComparisonPanel
						productKey={productKey}
						standardPrice={standardPrice}
						premiumPrice={premiumPrice}
					/>
				}
				actions={
					<>
						<Button appearance="subtle" onClick={onClose}>
							{formatMessage(messages.maybeLater)}
						</Button>
						<Button appearance="primary" onClick={onClickBuyStandard}>
							{formatMessage(messages.buyStandard)}
						</Button>
					</>
				}
				backgroundColor="color.background.input"
			>
				<PriceContrast standardPrice={standardPrice} premiumPrice={premiumPrice} />
				<FireScreenAnalytics />
			</Modal>
		</ContextualAnalyticsData>
	);
};
